import { useContext, useState, useCallback, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material/styles";
import AddComponent from "./add-component";
import ConfirmationStep from "./confirmation-step";
import { useService } from "../organizations-service";
import pubSub, { EVENTS } from "../../../config/pubSub";
import { IAddEditProviderModel } from "../organization-upsert-form";
import { OrganizationsContext } from "../organizations-context";
import Typography from "@mui/material/Typography";
import { StyledButton } from "../../../components/styled-button/styled-button";

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(4),
  paddingBottom: "0px",
  display: "flex",
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
  color: "var(--kyocera-dark-grey)",
  fontFamily: "Noto Sans",
  fontSize: "36px",
  fontWeight: "bolder",
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(4),
}));

const AddOrganizationComponent = () => {
  const [openAddOrganization, setOpenAddOrganization] = useState<boolean>(false);
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);
  const { orgService } = useService();
  const [isAllowAddChild, setIsAllowAddChild] = useState<boolean>(false);
  const { isOrgListLoading, selectedOrg, setSelectedOrg} = useContext(OrganizationsContext);
  const { t } = useTranslation();

  const checkOrgLimit = useCallback(async () => {
    try {
      if (!orgService.getOrg) return;
      const res: any = await orgService.getOrgLimit();
      setIsAllowAddChild(res.childrenAdd);
    } catch (error) {
      pubSub.publish(EVENTS.ERROR, "common_error_msg");
    }
  }, [orgService]);

  useEffect(() => {
    checkOrgLimit();
  }, [checkOrgLimit]);

  const handleAddClickOpen = () => {
    checkOrgLimit();
    setOpenAddOrganization(isAllowAddChild);

    if (selectedOrg)
      setSelectedOrg("");
  };

  const handleAddOrganizationDone = () => {
    setOpenAddOrganization(false);
    setOpenConfirm(true);
  };

  return (
    <div>
      <StyledButton
        variant="contained"
        onClick={handleAddClickOpen}
        className="d-flex align-items-center"
        disabled={!isAllowAddChild || isOrgListLoading}
        startIcon={<AddCircleOutlineOutlinedIcon />}
      >
        {t("cmp_organization_add_button")}
      </StyledButton>
      <AddOrganizationStep
        open={openAddOrganization}
        handleAddOrganizationClose={() => setOpenAddOrganization(false)}
        handleAddOrganizationDone={handleAddOrganizationDone}
      />
      <ConfirmationStep
        open={openConfirm}
        handleConfirmClose={() => setOpenConfirm(false)}
      />
    </div>
  );
};

type IAddOrganizationProps = {
  open: boolean;
  handleAddOrganizationClose: () => void;
  handleAddOrganizationDone: () => void;
};

const AddOrganizationStep = (props: IAddOrganizationProps) => {
  const { t } = useTranslation();
  const { setSelectedOrg, setSelectedCheckedOrgs } = useContext(OrganizationsContext);

  const defaultModel: IAddEditProviderModel = {
    0: {
      name: "",
      displayName: "",
      email: "",
      url: "",
      locale: "en",
    },
    isValid: false
  };

  const [model, setModel] = useState<IAddEditProviderModel>(defaultModel);

  const handleClose = () => {
    setSelectedOrg(null);
    setSelectedCheckedOrgs([]);
    props.handleAddOrganizationClose();
    setModel(defaultModel);
  };

  const handleModelChange = (updatedModel: any) => {
    const updated = updatedModel;
    setModel((prevModel) => ({
      ...prevModel,
      ...updated,
    }));
  };

  return (
    <Dialog
      open={props.open}
      PaperProps={{
        style: { maxWidth: '701px' }, // Set custom max width
          }}
      fullWidth={true}
      onClose={handleClose}
      aria-labelledby="form-dialog-title"
    >
      <StyledDialogTitle id="form-dialog-title">
        <StyledTypography>{t("cmp_organization_add_button")}</StyledTypography>
      </StyledDialogTitle>
      <StyledDialogContent>
        <AddComponent
          handleDone={() => {
            handleClose();
            props.handleAddOrganizationDone();
          }}
          handleClose={handleClose}
          model={model}
          handleModelChange={handleModelChange}
        />
      </StyledDialogContent>
    </Dialog>
  );
};

export default AddOrganizationComponent;
